import * as Cesium from 'cesium';
import constellationsRaw from '../../data/constellations.json';
import {
  CELESTIAL_SPHERE_RADIUS_M,
  raDecToInertialUnitVector,
  validateConstellationsPayload,
  type ConstellationSpec,
} from './constellationMath';

/**
 * Major-constellation overlay: thin polyline "stick figures" plus
 * a centered name label, rendered at the celestial sphere's
 * effective radius so they appear glued to the BSC5 starfield
 * cubemap from any camera position.
 *
 * Lifecycle:
 *   - `attach(viewer)`   builds the polyline + label collections,
 *                         parses + freezes each star's J2000 unit
 *                         vector, registers a `preRender` callback.
 *   - `setVisible(bool)` toggles primitive visibility cheaply.
 *   - `detach()`         removes primitives + the preRender hook.
 *                         Idempotent.
 *
 * Each frame, the preRender callback gets Cesium's clock time,
 * computes the TEME→pseudo-fixed rotation matrix (no async EOP
 * dependency, accurate to a few arcseconds), and applies it to
 * each cached star unit vector to land an updated ECEF position.
 * Cost: O(n) where n ≈ 100 line endpoints + 10 labels — well under
 * a millisecond per frame.
 *
 * Data source: `resources/data/constellations.json` (a curated
 * starter set of 10 well-known constellations). Extend the JSON
 * with more entries; the layer picks them up at next reload.
 */
export class ConstellationLayer {
  private viewer: Cesium.Viewer | null = null;
  private polylines: Cesium.PolylineCollection | null = null;
  private labels: Cesium.LabelCollection | null = null;
  /** Per-line cached unit vectors in the inertial frame. Each entry
   *  is two unit vectors (start + end of one segment). Computed once
   *  at attach time; the preRender loop rotates them into ECEF and
   *  scales by the celestial-sphere radius to produce live positions. */
  private lineUnitVectors: Array<{
    start: [number, number, number];
    end:   [number, number, number];
  }> = [];
  /** Per-label cached unit vector + reference to the Cesium primitive
   *  so the preRender loop can update its `position`. */
  private labelEntries: Array<{
    unit: [number, number, number];
    primitive: Cesium.Label;
  }> = [];
  /** Per-line reference to the Cesium polyline so positions update
   *  in place each frame. */
  private polylinePrimitives: Cesium.Polyline[] = [];
  /** Subscription returned by `scene.preRender.addEventListener`. */
  private preRenderRemove: (() => void) | null = null;
  /** Scratch matrix to avoid per-frame allocation. */
  private readonly rotationScratch = new Cesium.Matrix3();
  /** Whether the user's overlay toggle currently wants this layer shown. */
  private visible = false;

  /** Build the polyline + label collections from `constellations.json`. */
  attach(viewer: Cesium.Viewer): void {
    if (this.viewer !== null) return;
    this.viewer = viewer;

    const payload = validateConstellationsPayload(constellationsRaw);
    if (payload === null) {
      console.warn('[ConstellationLayer] constellations.json failed validation; layer disabled');
      return;
    }

    const polylines = viewer.scene.primitives.add(
      new Cesium.PolylineCollection(),
    ) as Cesium.PolylineCollection;
    const labels = viewer.scene.primitives.add(
      new Cesium.LabelCollection(),
    ) as Cesium.LabelCollection;
    this.polylines = polylines;
    this.labels    = labels;

    // Color tokens picked to read against the dark space-themed bg
    // without competing with the Cesium-rendered Sun/Moon or the
    // selected-satellite pulse. Both are slightly translucent so a
    // dense star background still bleeds through.
    const lineColor  = Cesium.Color.fromCssColorString('#8a96b3').withAlpha(0.45);
    const labelColor = Cesium.Color.fromCssColorString('#b8c4dc').withAlpha(0.85);

    for (const c of payload.constellations) {
      this.buildConstellation(c, polylines, labels, lineColor, labelColor);
    }

    // Visibility defaults to false; the overlay-toggle subscriber in
    // Globe.ts calls setVisible(true) when the user flips the row on.
    polylines.show = false;
    labels.show    = false;

    // Per-frame transform. Cesium's `Scene.preRender` event fires
    // before each render with the current `JulianDate`; that's the
    // canonical place to update inertial-frame primitives.
    const remove = viewer.scene.preRender.addEventListener((_scene, time) => {
      if (!this.visible) return;
      this.updatePositions(time);
    });
    this.preRenderRemove = remove as unknown as () => void;
  }

  /** Show or hide the overlay. Cheap — toggles the collections'
   *  `show` flags, no rebuild. */
  setVisible(visible: boolean): void {
    this.visible = visible;
    if (this.polylines !== null) this.polylines.show = visible;
    if (this.labels !== null)    this.labels.show    = visible;
    // Force one position update on flip-to-on so the user doesn't
    // see a frame of stale positions before the next preRender tick.
    if (visible && this.viewer !== null) {
      this.updatePositions(this.viewer.clock.currentTime);
    }
  }

  /** True iff currently attached. */
  isAttached(): boolean {
    return this.viewer !== null;
  }

  /** Remove primitives + preRender hook. Idempotent. */
  detach(): void {
    if (this.preRenderRemove !== null) {
      this.preRenderRemove();
      this.preRenderRemove = null;
    }
    if (this.viewer !== null && !this.viewer.isDestroyed()) {
      if (this.polylines !== null) this.viewer.scene.primitives.remove(this.polylines);
      if (this.labels !== null)    this.viewer.scene.primitives.remove(this.labels);
    }
    this.polylines = null;
    this.labels = null;
    this.lineUnitVectors = [];
    this.labelEntries = [];
    this.polylinePrimitives = [];
    this.viewer = null;
    this.visible = false;
  }

  // ─── Internals ────────────────────────────────────────────────────

  private buildConstellation(
    spec: ConstellationSpec,
    polylines: Cesium.PolylineCollection,
    labels: Cesium.LabelCollection,
    lineColor: Cesium.Color,
    labelColor: Cesium.Color,
  ): void {
    // Pre-compute every star's unit vector once.
    const starVecs = spec.stars.map((s) => raDecToInertialUnitVector(s.ra, s.dec));

    for (const [a, b] of spec.lines) {
      const start = starVecs[a];
      const end   = starVecs[b];
      // Initial positions get overwritten on the first preRender tick;
      // any non-NaN cartesian works to satisfy Cesium's constructor.
      const startPos = this.scaledVectorToCartesian(start, Cesium.Matrix3.IDENTITY);
      const endPos   = this.scaledVectorToCartesian(end,   Cesium.Matrix3.IDENTITY);
      const polyline = polylines.add({
        positions: [startPos, endPos],
        width: 1.0,
        material: Cesium.Material.fromType(Cesium.Material.ColorType, { color: lineColor }),
      });
      this.lineUnitVectors.push({ start, end });
      this.polylinePrimitives.push(polyline);
    }

    const labelUnit = raDecToInertialUnitVector(spec.label.ra, spec.label.dec);
    const labelPos = this.scaledVectorToCartesian(labelUnit, Cesium.Matrix3.IDENTITY);
    const primitive = labels.add({
      position: labelPos,
      text: spec.name,
      font: '14px "Inter", sans-serif',
      fillColor: labelColor,
      outlineColor: Cesium.Color.BLACK.withAlpha(0.4),
      outlineWidth: 2,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      verticalOrigin: Cesium.VerticalOrigin.CENTER,
      horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
    });
    this.labelEntries.push({ unit: labelUnit, primitive });
  }

  /** Rotate every cached inertial unit vector into ECEF by the
   *  current TEME→pseudo-fixed matrix, scale to the celestial-
   *  sphere radius, and overwrite the live primitive positions. */
  private updatePositions(time: Cesium.JulianDate): void {
    const matrix = Cesium.Transforms.computeTemeToPseudoFixedMatrix(time, this.rotationScratch);
    if (matrix === undefined) return; // shouldn't happen — TEME variant is always available

    for (let i = 0; i < this.lineUnitVectors.length; i++) {
      const seg = this.lineUnitVectors[i];
      const startPos = this.scaledVectorToCartesian(seg.start, matrix);
      const endPos   = this.scaledVectorToCartesian(seg.end,   matrix);
      this.polylinePrimitives[i].positions = [startPos, endPos];
    }
    for (const entry of this.labelEntries) {
      entry.primitive.position = this.scaledVectorToCartesian(entry.unit, matrix);
    }
  }

  /** Compute `matrix · unit · radius` as a `Cartesian3`. New
   *  allocation each call — callers swap the result into primitive
   *  position slots, and Cesium hangs onto it as long as the slot
   *  references it. */
  private scaledVectorToCartesian(
    unit: [number, number, number],
    matrix: Cesium.Matrix3,
  ): Cesium.Cartesian3 {
    const v = new Cesium.Cartesian3(unit[0], unit[1], unit[2]);
    Cesium.Matrix3.multiplyByVector(matrix, v, v);
    return Cesium.Cartesian3.multiplyByScalar(v, CELESTIAL_SPHERE_RADIUS_M, v);
  }
}
